/** Stable client id for offer lines / delivery keys. Works over HTTP (no secure-context crypto). */
export function newClientKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `ck_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

export const numberValue = (value) => {
  const parsed = Number(String(value ?? "").replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};
/** Priced customer lines only — notes/text and chapters are not quantity/price checked. */
export const isPriceLine = (row) =>
  row.item_type !== "group" &&
  row.item_type !== "text" &&
  !row.group_row &&
  !row.delete;
export const includedLine = (row) => isPriceLine(row) && row.visible_to_customer !== false && !["optional", "excluded"].includes(row.pricing_role);

/** Additive time on product lines — unit = product price + hours × rate. */
const PRODUCT_PLUS_HOURS_MODES = new Set([
  "product_plus_hours",
  "additive",
  "hours_in_addition",
]);

function hoursPricingMode(row) {
  return row?.hours_pricing_mode || row?.custom_fields?.hours_pricing_mode || null;
}

function isProductPlusHoursLine(row) {
  if (!row || row.line_type === "hours") return false;
  const mode = String(hoursPricingMode(row) || "");
  if (PRODUCT_PLUS_HOURS_MODES.has(mode)) return true;
  if (mode === "from_price" || mode === "hours_only") return false;
  if (row.line_type !== "product") return false;
  return numberValue(row.hours) > 0 && numberValue(row.hour_rate) > 0;
}

/** Mirror of calculation-items `calculateAutomaticSalesPrice` (unit for price_mode automatic). */
function automaticLineUnit(row) {
  let total = 0;
  const qty = numberValue(row?.quantity);
  const nestedAmount = numberValue(row?.nested_calculation?.total_amount);
  if (nestedAmount) {
    const divide =
      row?.nested_calculation?.divide_by_quantity || row?.divide_by_quantity;
    total += divide && qty > 0 ? nestedAmount / qty : nestedAmount;
  }
  if (Array.isArray(row?.products)) {
    row.products.forEach((prod) => {
      const prodQty = numberValue(prod.quantity) || 1;
      let prodSales = numberValue(prod.sales_price);
      if (prod.divide_by_quantity && qty > 0) prodSales /= qty;
      if (prodSales > 0 && prodQty > 0) {
        total +=
          prodSales *
          prodQty *
          (1 + Math.max(0, numberValue(prod.surcharge)) / 100) *
          (1 - Math.max(0, numberValue(prod.discount)) / 100);
      }
    });
  }
  let hours = numberValue(row?.hours);
  const hourRate = numberValue(row?.hour_rate);
  if (row?.divide_by_quantity && qty > 0) hours /= qty;
  if (hours > 0 && hourRate > 0) {
    // Unit price only — lineAmount multiplies by quantity afterwards.
    total += hours * hourRate;
  }
  return total;
}

export function lineAmount(row) {
  const nestedId = row?.nested_calculation_id ?? row?.nested_calculation?.id;
  const nestedUnit = row?.nested_calculation?.total_amount;
  const hasNested = nestedId != null && nestedUnit != null && nestedUnit !== "";
  let unit;
  if (row?.price_mode === "automatic") {
    unit = automaticLineUnit(row);
  } else if (hasNested) {
    unit = numberValue(nestedUnit);
  } else {
    unit = numberValue(row.sales_price);
    if (isProductPlusHoursLine(row)) {
      const hours = numberValue(row.hours);
      const hourRate = numberValue(row.hour_rate);
      if (hours > 0 && hourRate > 0) unit += hours * hourRate;
    }
  }
  return (
    numberValue(row.quantity) *
    unit *
    (1 + Math.max(0, numberValue(row.surcharge)) / 100) *
    (1 - Math.max(0, numberValue(row.discount)) / 100)
  );
}

/** Sum priced calc lines (surcharge then discount) for live offer header / KPI. */
export function calculationSalesTotal(calc) {
  return (calc?.items || []).reduce((sum, row) => {
    if (row?.delete === true) return sum;
    const type = row?.item_type || row?.type;
    if (type === "group" || type === "text" || row?.group_row === true) return sum;
    if (row?.line_type === "note") return sum;
    return sum + lineAmount(row);
  }, 0);
}
export function offerChecks(offer, items = offer?.items || []) {
  const lines = items.filter(includedLine);
  const checks = [];
  if ((offer?.calculations || []).some((calculation) => calculation.module_result?.errors?.length)) checks.push({ code: "calculation", tab: "kalkyle", blocking: true });
  if (!offer?.customer_id && !offer?.customer?.id) checks.push({ code: "customer", tab: "oversikt", blocking: true });
  // ADR-005: kalkyle eier innhold — tomme kundelinjer er rådgivende når kalkyle finnes
  // (materialiseres ved revisjon/send). Uten kalkyle forblir det blokkerende.
  // Open → Kalkyle when calc-related (not Customer view / tilbudslinjer).
  const hasCalculation = (offer?.calculations || []).length > 0;
  const contentTab = hasCalculation ? "kalkyle" : "tilbudslinjer";
  if (!lines.length) {
    checks.push({
      code: "empty",
      tab: contentTab,
      blocking: !hasCalculation,
    });
  }
  if (lines.some((row) => !String(row.title || row.description || "").trim())) checks.push({ code: "description", tab: contentTab, blocking: true });
  if (lines.some((row) => numberValue(row.sales_price) === 0)) checks.push({ code: "price", tab: contentTab, blocking: false });
  if (lines.some((row) => numberValue(row.quantity) <= 0)) checks.push({ code: "quantity", tab: contentTab, blocking: true });
  const includedByGroup = {};
  lines.forEach((row) => {
    const group = String(row.alternative_group || "").trim();
    if (!group) return;
    includedByGroup[group] = (includedByGroup[group] || 0) + 1;
  });
  if (Object.values(includedByGroup).some((count) => count > 1)) {
    checks.push({ code: "alternatives", tab: contentTab, blocking: true });
  }
  if (offer?.valid_to && new Date(offer.valid_to).getTime() < new Date().setHours(0, 0, 0, 0)) checks.push({ code: "expired", tab: "oversikt", blocking: false });
  const sales = lines.reduce((sum, row) => sum + lineAmount(row), 0);
  if (offer?.cost_total != null && numberValue(offer.cost_total) > sales) checks.push({ code: "margin", tab: "kalkyle", blocking: false });
  return checks;
}
export function parseOfferPaste(text) {
  const rows = text.trim().split(/\r?\n/).filter(Boolean);
  return rows.map((line) => {
    const [title, quantity, price] = line.split("\t");
    if (!title?.trim() || quantity == null || price == null || !Number.isFinite(Number(quantity.replace(",", "."))) || !Number.isFinite(Number(price.replace(/\s/g, "").replace(",", ".")))) throw new Error("invalid_paste");
    return { title: title.trim(), quantity: numberValue(quantity), sales_price: numberValue(price), discount: 0, surcharge: 0, item_type: "manual", visible_to_customer: true, pricing_role: "included" };
  });
}
